import React, { useState, useRef, useCallback } from 'react';
import { Box, Button, Typography } from '@material-ui/core';
import { Header } from '../../../components/Header';
import ProvisionStepper from '../provisionStepper';
import Icon from '../../../utils/icons';
import I18n from '../../../localization';
import makeStyle from './installationGuideStyles';
import HCServices from '../../../services/hcServices';
import { IQ_AIR_CONTACT_SUPPORT,PAGES } from '../../../utils/constant';
import NoCWireFlow, { PP_STEPS, NO_C_WIRE_STEPS } from './NoCWireFlow';
import { buildWiringPayload, normalizeWiringMap, adjustMapForDisplayType } from './wiringUtils';


const STEPS = {
    INTRO: 'INTRO',
    WHATS_IN_BOX: 'WHATS_IN_BOX',
    TOOLS_NEEDED: 'TOOLS_NEEDED',
    TURN_OFF_HVAC: 'TURN_OFF_HVAC',
    HVAC_POWER_ERROR: 'HVAC_POWER_ERROR',
    TAKE_PHOTO: 'TAKE_PHOTO',
    PHOTO_ERROR: 'PHOTO_ERROR',
    HIGH_VOLTAGE: 'HIGH_VOLTAGE',
    HIGH_VOLTAGE_ERROR: 'HIGH_VOLTAGE_ERROR',
    JUMPER_WIRES: 'JUMPER_WIRES',
    REMOVE_JUMPERS: 'REMOVE_JUMPERS',
    DUAL_LABELS: 'DUAL_LABELS',
    HEAT_PUMP_CHECK: 'HEAT_PUMP_CHECK',
    TERMINAL_SELECTION: 'TERMINAL_SELECTION',
    UNCOMMON_WIRING: 'UNCOMMON_WIRING',
    K_TERMINAL: 'K_TERMINAL',
    UNRECOGNIZED: 'UNRECOGNIZED',
    POWER_PLATE: 'POWER_PLATE',
    UW_TAKE_PHOTO: 'UW_TAKE_PHOTO',
    UW_GOTO_HVAC: 'UW_GOTO_HVAC',
    UW_OPEN_HVAC: 'UW_OPEN_HVAC',
    UW_TERMINAL_STRIP: 'UW_TERMINAL_STRIP',
    UW_FIND_WIRE_COLOR: 'UW_FIND_WIRE_COLOR',
    UW_COMMON_TERMINAL: 'UW_COMMON_TERMINAL',
    UW_COMMON_TERMINAL_2: 'UW_COMMON_TERMINAL_2',
    UW_CLOSE_RETURN: 'UW_CLOSE_RETURN',
    REMOVE_THERMOSTAT: 'REMOVE_THERMOSTAT',
    INSTALL_TRIM_PLATE: 'INSTALL_TRIM_PLATE',
    MOUNT_BASE: 'MOUNT_BASE',
    CONNECT_WIRES: 'CONNECT_WIRES',
    RESTORE_POWER: 'RESTORE_POWER',
    CHECK_LED: 'CHECK_LED',
    LED_ERROR: 'LED_ERROR',
    ATTACH_DISPLAY: 'ATTACH_DISPLAY',
    ON_SCREEN_INSTRUCTIONS: 'ON_SCREEN_INSTRUCTIONS',
    SERVICE_PROVIDER_PIN: 'SERVICE_PROVIDER_PIN',
    SETUP_COMPLETE: 'SETUP_COMPLETE',
    JUMPER_EXPLAIN: 'JUMPER_EXPLAIN',
    HEAT_PUMP_EXPLAIN: 'HEAT_PUMP_EXPLAIN',
    DUAL_FUEL_EXPLAIN: 'DUAL_FUEL_EXPLAIN',
    ...PP_STEPS,
};


const TERMINAL_GROUPS_MAIN = [
    { label: 'Power', terminals: ['R', 'Rh', 'Rc'] },
    { label: 'Common', terminals: ['C'] },
    { label: 'Heating', terminals: ['W or W1', 'W2'] },
    { label: 'Cooling', terminals: ['Y or Y1', 'Y2'] },
    { label: 'Fan', terminals: ['G or F'] },
    { label: 'heat pump', terminals: ['O', 'B', 'O/B'] },
];

const TERMINAL_GROUP_ACCESSORIES = {
    label: 'Other / Accessories',
    terminals: ['Aux', 'E', 'W3', 'X', 'L', 'K', 'dh', 'T', 'COM', 'S1 or S2 or S3', 'Z1', 'Z2', '*', 'U1', 'ACC+', 'GND', 'ODT', 'Other'],
};


const InstallationGuide = ({ onBack, onViewSystem , isIQAirDisplay,handleClick,isDarkMode, displayType, openURL }) => {
    const classes = makeStyle();
    const [currentStep, setCurrentStep] = useState(STEPS.INTRO);
    const [history, setHistory] = useState([]);
    const [capturedImage, setCapturedImage] = useState(null);
    const [capturedImageName, setCapturedImageName] = useState(null);
    const [selectedTerminals, setSelectedTerminals] = useState([]);
    const [showAccessories, setShowAccessories] = useState(false);
    const [wiringMap, setWiringMap] = useState([]);
    const [isHeatPump, setIsHeatPump] = useState(null);
    const [unusedWirePhoto, setUnusedWirePhoto] = useState(null);
    const [unusedWirePhotoName, setUnusedWirePhotoName] = useState(null);
    const [isPowerPlateFlow, setIsPowerPlateFlow] = useState(false);
    const [isDualRNoC, setIsDualRNoC] = useState(false);
    const [ppWireColors, setPpWireColors] = useState({});
    const [ppCustomColors, setPpCustomColors] = useState({});
    const [isPhotoExpanded, setIsPhotoExpanded] = useState(false);
    const [expandedPhotoSrc, setExpandedPhotoSrc] = useState(null);

    const imgZoomRef = useRef(null);
    const zoomState = useRef({ scale: 1, lastDist: null, originX: 0, originY: 0, translateX: 0, translateY: 0 });

    const applyTransform = useCallback(() => {
        const el = imgZoomRef.current;
        if (!el) return;
        const { scale, translateX, translateY } = zoomState.current;
        el.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
    }, []);

    const handleOverlayTouchStart = useCallback((e) => {
        if (e.touches.length === 2) {
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            zoomState.current.lastDist = Math.hypot(dx, dy);
            zoomState.current.originX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
            zoomState.current.originY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        }
    }, []);

    const handleOverlayTouchMove = useCallback((e) => {
        if (e.touches.length === 2) {
            e.preventDefault();
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            const dist = Math.hypot(dx, dy);
            if (zoomState.current.lastDist) {
                const delta = dist / zoomState.current.lastDist;
                zoomState.current.scale = Math.min(5, Math.max(1, zoomState.current.scale * delta));
                applyTransform();
            }
            zoomState.current.lastDist = dist;
        }
    }, [applyTransform]);

    const handleOverlayTouchEnd = useCallback(() => {
        zoomState.current.lastDist = null;
        if (zoomState.current.scale <= 1) {
            zoomState.current.scale = 1;
            zoomState.current.translateX = 0;
            zoomState.current.translateY = 0;
            applyTransform();
        }
    }, [applyTransform]);

    const openPhoto = (src) => {
        zoomState.current = { scale: 1, lastDist: null, originX: 0, originY: 0, translateX: 0, translateY: 0 };
        if (imgZoomRef.current) imgZoomRef.current.style.transform = 'none';
        setExpandedPhotoSrc(src);
        setIsPhotoExpanded(true);
    };

    const resetZoom = () => {
        zoomState.current = { scale: 1, lastDist: null, originX: 0, originY: 0, translateX: 0, translateY: 0 };
        if (imgZoomRef.current) imgZoomRef.current.style.transform = 'none';
        setIsPhotoExpanded(false);
        setExpandedPhotoSrc(null);
    };

    const goTo = (step) => {
        setHistory(prev => [...prev, currentStep]);
        setCurrentStep(step);
    };

    const goBack = () => {
        if (history.length === 0) {
            onBack();
            return;
        }
        const prev = history[history.length - 1];
        setHistory(h => h.slice(0, -1));
        setCurrentStep(prev);
    };

    const headerTitle = currentStep === STEPS.SETUP_COMPLETE
        ? I18n.t('mobile.AirDeviceProvisioning.completeSystemSetup', { defaultValue: 'Complete System setup' })
        : I18n.t('mobile.AirDeviceProvisioning.installIQAirTitle', { defaultValue: 'Install IQ Air' });

    // Steps 1-3 always completed; wrench gray (pending) throughout guide, completed only on SETUP_COMPLETE
    const isComplete = currentStep === STEPS.SETUP_COMPLETE;
    const activeStep = isComplete ? 4 : 3;

    // ── Photo state setters by target key ────────────────────────────────────
    const photoSetters = {
        thermostat: { setImage: setCapturedImage, setName: setCapturedImageName },
        unusedWire: { setImage: setUnusedWirePhoto, setName: setUnusedWirePhotoName },
    };

    // ── Shared camera / photo capture ─────────────────────────────────────────
    const capturePhoto = (target) => {
        const { setImage, setName } = photoSetters[target];
        if (window.navigator && window.navigator.camera) {
            window.navigator.camera.getPicture(
                (fileUri) => {
                    const timestamp = Date.now();
                    setName(`img_${timestamp}.jpg`);
                    setImage(fileUri);
                },
                (err) => console.warn('Camera capture error:', err),
                {
                    quality: 70,
                    destinationType: 1,
                    sourceType: 1,
                    encodingType: 0,
                    targetWidth: 600,
                    targetHeight: 600,
                    correctOrientation: true,
                    saveToPhotoAlbum: true,
                }
            );
        } else {
            document.getElementById(`ig-capture-${target}`).click();
        }
    };

    // ── Shared upload from gallery/file system ────────────────────────────────
    const uploadPhoto = (target) => {
        document.getElementById(`ig-upload-${target}`).click();
    };

    // ── Shared file input onChange handler ─────────────────────────────────────
    const handleFileSelect = (target, e) => {
        const file = e.target.files[0];
        if (file) {
            const { setImage, setName } = photoSetters[target];
            setName(file.name);
            const reader = new FileReader();
            reader.onload = (ev) => setImage(ev.target.result);
            reader.onerror = () => {
                console.error('Failed to read file:', file.name);
                setName(null);
                setImage(null);
            };
            reader.readAsDataURL(file);
        }
        e.target.value = '';
    };

    // ── Terminal chip toggle ──────────────────────────────────────────────────
    const toggleTerminal = (t) => {
        setSelectedTerminals(prev =>
            prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]
        );
    };

    // ── Map API validation-status to screen routing ───────────────────────────
    const mapStatusToScreen = (status) => {
        switch (status) {
            case 'UNRECOGNIZED_TERMINAL': return 'S5';
            case 'K_TERMINAL_DETECTED': return 'S4';
            case 'INVALID_WIRING_COMBINATION': return 'S3';
            case 'VALID_NO_C_WIRE': return 'POWER_PLATE';
            case 'VALID_NO_C_WIRE_DUAL_R': return 'POWER_PLATE_DUAL_R';
            case 'VALID': return 'CONNECT_WIRES';
            default: return 'S3';
        }
    };

    // ── Terminal Continue → routing ───────────────────────────────────────────
    const handleTerminalContinue = async () => {
        try {
            const payload = buildWiringPayload(selectedTerminals);
            const response = await HCServices.validateWiring(payload);
            if (response.status === 'failure') { goTo(STEPS.UNCOMMON_WIRING); return; }
            const data = response.data || response;
            const validationStatus = data['validation-status'];
            const screen = mapStatusToScreen(validationStatus);
            if (screen === 'S3') { goTo(STEPS.UNCOMMON_WIRING); return; }
            if (screen === 'S4') { goTo(STEPS.K_TERMINAL); return; }
            if (screen === 'S5') { goTo(STEPS.UNRECOGNIZED); return; }
            const map = adjustMapForDisplayType(normalizeWiringMap(data['wiring-map']), displayType);
            if (map) setWiringMap(map);
            if (screen === 'POWER_PLATE') { setIsDualRNoC(false); goTo(STEPS.POWER_PLATE); return; }
            if (screen === 'POWER_PLATE_DUAL_R') { setIsDualRNoC(true); goTo(STEPS.POWER_PLATE); return; }
            goTo(STEPS.REMOVE_THERMOSTAT);
        } catch (error) {
            console.error('Wiring validation failed:', error);
            goTo(STEPS.UNCOMMON_WIRING);
        }
    };

    // ── Unused wire (UW) flow completion → rebuild wiring map with C wire ────
    const handleUnusedWireComplete = async () => {
        try {
            const payload = buildWiringPayload([...selectedTerminals, 'C']);
            const response = await HCServices.validateWiring(payload);
            if (response.status === 'failure') { goTo(STEPS.UNCOMMON_WIRING); return; }
            const data = response.data || response;
            const validationStatus = data['validation-status'];
            const screen = mapStatusToScreen(validationStatus);
            if (screen !== 'CONNECT_WIRES' && screen !== 'POWER_PLATE' && screen !== 'POWER_PLATE_DUAL_R') {
                goTo(STEPS.UNCOMMON_WIRING); return;
            }
            const rawMap = adjustMapForDisplayType(normalizeWiringMap(data['wiring-map'] || []), displayType);
            if (!rawMap || rawMap.length === 0) { goTo(STEPS.UNCOMMON_WIRING); return; }
            const completedMap = rawMap.map(row =>
                row['new'] === 'C' ? { ...row, old: '\u2014', note: true } : row
            );
            setWiringMap(completedMap);
            goTo(STEPS.REMOVE_THERMOSTAT);
        } catch (error) {
            console.error('Unused wire validation failed:', error);
            goTo(STEPS.UNCOMMON_WIRING);
        }
    };

    // ── Shared stepper progress steps ────────────────────────────────────────
    const progressSteps = { step1: 100, step2: 100, step3: 100 };

    // ── Render helpers ────────────────────────────────────────────────────────
    const renderStepper = () => {
        if(isIQAirDisplay){
         return null
        }
       return <ProvisionStepper
            key={activeStep}
            activeStep={activeStep}
            progressSteps={progressSteps}
            incomplete={!isComplete}
        />
    }

    const renderImage = (src,darkModeImg=false) => {
        
      return  <Box className={classes.imageWrapper}>
            <Icon src={(darkModeImg ? `${src}Dark` : src) || 'thermostatImage'} alt="Installation" />
        </Box>
    };

    const renderBottomContinue = (onPress, label = 'Continue', disabled = false) => (
        <Box className={classes.buttonContainer}>
            <Button className={classes.primaryButton} onClick={onPress} disabled={disabled}>
                {label}
            </Button>
        </Box>
    );

    const renderYesNo = (onNo, onYes) => (
        <Box className={classes.buttonContainer}>
            <Box className={classes.yesNoRow}>
                <Button className={classes.yesNoButton} onClick={onNo}>
                    {I18n.t('mobile.AirDeviceProvisioning.no', { defaultValue: 'No' })}
                </Button>
                <Button className={classes.yesButton} onClick={onYes}>
                    {I18n.t('mobile.AirDeviceProvisioning.yes', { defaultValue: 'Yes' })}
                </Button>
            </Box>
        </Box>
    );

    const renderBackOnly = () => (
        <Box className={classes.buttonContainer}>
            <Button className={classes.secondaryButton} onClick={goBack}>
                {I18n.t('mobile.AirDeviceProvisioning.retryBtn', { defaultValue: 'Retry' })}
            </Button>
        </Box>
    );

    const renderRetryAndContactSupport = () => (
        <Box className={classes.buttonContainer}>
            <Button className={classes.yesButton} onClick={goBack}>
                {I18n.t('mobile.AirDeviceProvisioning.retryBtn', { defaultValue: 'Retry' })}
            </Button>
            <Button className={classes.yesNoButton} onClick={() => openURL(IQ_AIR_CONTACT_SUPPORT)}>
                {I18n.t('mobile.AirDeviceProvisioning.contactSupport', { defaultValue: 'Contact Support' })}
            </Button>
        </Box>
    );

    const renderErrorScreen = (title, body, showSupportButton = false) => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('IgThermostatError')}
                <Typography className={classes.title}>{title}</Typography>
                <Typography className={classes.subtitle}>{body}</Typography>
            </Box>
            {showSupportButton ? renderRetryAndContactSupport() :renderBackOnly()}
        </>
    );

    // ── Screen renderers ──────────────────────────────────────────────────────
    const renderIntro = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('thermostatImage')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.installationOverviewTitle', { defaultValue: 'Installation overview' })}
                </Typography>
                
                <Typography dangerouslySetInnerHTML={{
                    __html: I18n.t('mobile.AirDeviceProvisioning.installationOverviewSubtitleCommon', { defaultValue: 'Tap Start Installation to open step-by-step guide, which will walk you through:' }),
                }} className={`${classes?.subtitle} ${classes?.subtitleSecondary}`}>

                </Typography>
                <ol className={`${classes?.numberedList} ${classes?.numberedListNone}`}>
                    <li dangerouslySetInnerHTML={{__html: I18n.t('mobile.AirDeviceProvisioning.installStep1', { defaultValue: '1. Preparation: Turning off HVAC power and removing your old thermostat.' })}}></li>
                    <li dangerouslySetInnerHTML={{__html: I18n.t('mobile.AirDeviceProvisioning.installStep2', { defaultValue: '2. Hardware Setup: Wiring and mounting your new IQ Air base and display.' })}}></li>
                    <li dangerouslySetInnerHTML={{__html: I18n.t('mobile.AirDeviceProvisioning.installStep3', { defaultValue: '3. Activation: Completing the on-screen digital setup.' }),
                }}></li>
                </ol>
            </Box>
            {renderBottomContinue(() => goTo(STEPS.WHATS_IN_BOX),'Start Installation')}
            
                <Box className={classes.buttonContainer}>
                    <Button className={`${classes?.primaryButton} ${classes?.iqAirDisplayPrimaryButton}`} onClick={()=>goTo(STEPS.ON_SCREEN_INSTRUCTIONS)}>
                      {I18n.t('mobile.AirDeviceProvisioning.alreadyInstalled', { defaultValue: 'Already Installed?' })}
                    </Button>
                </Box>
            
        </>
    );

    const renderWhatsInBox = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igWhatsInBox')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.whatsInBoxTitle', { defaultValue: "What's in the box" })}
                </Typography>
                <ol className={classes.numberedList}>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.boxItem1', { defaultValue: 'Trim plate' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.boxItem2', { defaultValue: 'Base' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.boxItem3', { defaultValue: 'Display' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.boxItem4', { defaultValue: 'Drywall anchors × 2' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.boxItem5', { defaultValue: 'Wall screws × 2' })}</li>
                </ol>
            </Box>
            {renderBottomContinue(() => goTo(STEPS.TOOLS_NEEDED))}
        </>
    );

    const renderToolsNeeded = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igToolsNeeded')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.toolsNeededTitle', { defaultValue: 'Tools needed' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.toolsNeededSubtitle', { defaultValue: 'Have these tools ready for installation:' })}
                </Typography>
                <ol className={classes.numberedList}>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.tool1', { defaultValue: 'Phillips screwdriver' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.tool2', { defaultValue: 'Pencil to mark mounting location' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.tool3', { defaultValue: 'Power drill with 3/16" drill bit' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.tool4', { defaultValue: 'Needle-nose pliers' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.tool5', { defaultValue: 'Phone with camera and internet access' })}</li>
                </ol>
            </Box>
            {renderBottomContinue(() => goTo(STEPS.TURN_OFF_HVAC))}
        </>
    );

    const renderTurnOffHvac = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igTurnOffHvac')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.turnOffHvacTitle', { defaultValue: 'Turn off HVAC power' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.turnOffHvacBody', { defaultValue: 'Before checking wiring, turn off HVAC power to avoid shock or system damage.' })}
                </Typography>
                <Typography className={classes.subtitle} style={{ marginTop: '16px' }}>
                    {I18n.t('mobile.AirDeviceProvisioning.turnOffHvacQuestion', { defaultValue: 'Is the thermostat powered off?' })}
                </Typography>
            </Box>
            {renderYesNo(
                () => goTo(STEPS.HVAC_POWER_ERROR),
                () => goTo(STEPS.TAKE_PHOTO)
            )}
        </>
    );

    const renderHvacPowerError = () => renderErrorScreen(
        I18n.t('mobile.AirDeviceProvisioning.doNotProceedTitle', { defaultValue: 'Do not proceed' }),
        I18n.t('mobile.AirDeviceProvisioning.hvacPowerErrorBody', { defaultValue: 'Your HVAC system must be powered off before proceeding.' })
    );

    const renderTakePhoto = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igTakePhoto')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.takePhotoTitle', { defaultValue: 'Have you taken a photo?' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.takePhotoBody', { defaultValue: 'Remove the thermostat from the wall and take a photo of the wires and the terminal labels.' })}
                </Typography>
                {!capturedImage && <Box className={classes.photoHintBox}>
                    <Typography className={classes.photoHintText}>
                        {I18n.t('mobile.AirDeviceProvisioning.takePhotoHint', { defaultValue: 'This photo will help you when you connect the wiring later.' })}
                    </Typography>
                    <Box className={classes.photoButtonsContainer}>
                        <Button className={classes.takePhotoBtn} onClick={() => capturePhoto('thermostat')}>
                            {I18n.t('mobile.AirDeviceProvisioning.takePhotoBtn', { defaultValue: 'TAKE PHOTO' })}
                        </Button>
                        <Button className={classes.takePhotoBtn} onClick={() => uploadPhoto('thermostat')}>
                            {I18n.t('mobile.AirDeviceProvisioning.uploadBtn', { defaultValue: 'UPLOAD' })}
                        </Button>
                    </Box>
                </Box>}
                {capturedImage && (
                    <Box className={classes.capturedThumb}>
                        <Box className={classes.capturedThumbRow}>
                            <Typography className={classes.capturedThumbName}>
                                {capturedImageName?.substring(0, 5) + capturedImageName?.substring(capturedImageName.lastIndexOf('.'))}
                            </Typography>
                            <Button className={classes.closeThumbBtn} onClick={() => { setCapturedImage(null); setCapturedImageName(null); }}>✕</Button>
                        </Box>
                        <img src={capturedImage} alt="captured wiring" className={classes.capturedThumbImg} />
                    </Box>
                )}
                <input
                    id="ig-capture-thermostat"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileSelect('thermostat', e)}
                />
                <input
                    id="ig-upload-thermostat"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileSelect('thermostat', e)}
                />
            </Box>
            {renderYesNo(
                () => goTo(STEPS.PHOTO_ERROR),
                () => goTo(STEPS.HIGH_VOLTAGE)
            )}
        </>
    );

    const renderPhotoError = () => renderErrorScreen(
        I18n.t('mobile.AirDeviceProvisioning.doNotProceedTitle', { defaultValue: 'Do not proceed' }),
        I18n.t('mobile.AirDeviceProvisioning.photoErrorBody', { defaultValue: 'It is important that you take a clear photo of your old thermostat wires and terminal labels. This will be crucial while checking the HVAC compatibility in further steps.' })
    );

    const renderHighVoltage = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igHighVoltage')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.highVoltageTitle', { defaultValue: 'Do you see any of the following on your thermostat or wiring?' })}
                </Typography>
                <ol className={classes.numberedList}>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.highVoltageItem1', { defaultValue: 'Labels that say 120 V and 240 V, or L1/L2' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.highVoltageItem2', { defaultValue: 'Stranded wires instead of solid ones' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.highVoltageItem3', { defaultValue: 'Thick wires connected with wire nuts' })}</li>
                </ol>
            </Box>
            {renderYesNo(
                () => goTo(STEPS.JUMPER_WIRES),
                () => goTo(STEPS.HIGH_VOLTAGE_ERROR)
            )}
        </>
    );

    const renderHighVoltageError = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('IgThermostatError')}
                <Box className={classes.errorContainer}>
                    <Typography className={classes.title}>
                        {I18n.t('mobile.AirDeviceProvisioning.notCompatibleTitle', { defaultValue: 'Not compatible' })}
                    </Typography>
                    <Typography className={classes.subtitle}>
                        {I18n.t('mobile.AirDeviceProvisioning.highVoltageErrorBody', { defaultValue: 'Your system uses high-voltage wiring. The IQ Air is designed for 24 V systems and cannot be installed on high-voltage circuits.' })}
                    </Typography>
                </Box>
            </Box>
            {renderBackOnly()}
        </>
    );

    const renderJumperWires = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igJumperWires',isDarkMode)}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.jumperWiresTitle', { defaultValue: 'Do you see jumper wires?' })}
                </Typography>
                <Typography className={classes.subtitle} style={{ marginTop: '16px' }}>
                    {I18n.t('mobile.AirDeviceProvisioning.jumperWiresBody', { defaultValue: 'Some thermostats utilize jumper wires to connect two terminals together.' })}
                </Typography>
                <Typography className={classes.learnMoreLink} onClick={() => goTo(STEPS.JUMPER_EXPLAIN)}>
                    {I18n.t('mobile.AirDeviceProvisioning.learnMoreJumperWire', { defaultValue: 'Learn more about jumper wire' })}
                </Typography>
            </Box>
            {renderYesNo(
                () => goTo(STEPS.DUAL_LABELS),
                () => goTo(STEPS.REMOVE_JUMPERS)
            )}
        </>
    );

    const renderRemoveJumpers = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igJumperWires',isDarkMode)}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.removeJumpersTitle', { defaultValue: 'Remove all jumper wires' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.removeJumpersBody', { defaultValue: 'Use a screwdriver to remove the jumper wires. The IQ Air handles this connection internally, so you will not need it.' })}
                </Typography>
            </Box>
            {renderBottomContinue(() => goTo(STEPS.DUAL_LABELS))}
        </>
    );

    const renderDualLabels = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igDualLabels',isDarkMode)}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.dualLabelsTitle', { defaultValue: 'Do you see two sets of labels on your old thermostat?' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.dualLabelsBody', { defaultValue: 'Some terminals have two sets of labels, one for conventional and one for heat pump.' })}
                </Typography>
            </Box>
            {renderYesNo(
                () => goTo(STEPS.TERMINAL_SELECTION),
                () => goTo(STEPS.HEAT_PUMP_CHECK)
            )}
        </>
    );

    const renderHeatPumpCheck = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igHeatPumpCheck')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.heatPumpCheckTitle', { defaultValue: 'Do you have a Heat Pump?' })}
                </Typography>
                <Typography className={classes.learnMoreLink} style={{ marginTop: '24px' }} onClick={() => goTo(STEPS.HEAT_PUMP_EXPLAIN)}>
                    {I18n.t('mobile.AirDeviceProvisioning.learnMoreHeatPump', { defaultValue: 'Learn more about identifying your system type' })}
                </Typography>
            </Box>
            {renderYesNo(
                () => { setIsHeatPump(false); goTo(STEPS.TERMINAL_SELECTION); },
                () => { setIsHeatPump(true); goTo(STEPS.TERMINAL_SELECTION); }
            )}
        </>
    );

    const renderPhotoOverlay = () => isPhotoExpanded && expandedPhotoSrc ? (
        <Box
            className={classes.photoOverlay}
            onTouchStart={handleOverlayTouchStart}
            onTouchMove={handleOverlayTouchMove}
            onTouchEnd={handleOverlayTouchEnd}
        >
            <Button className={classes.photoOverlayClose} onClick={resetZoom}>✕</Button>
            <img
                ref={imgZoomRef}
                src={expandedPhotoSrc}
                alt="expanded"
                className={classes.photoOverlayImg}
            />
        </Box>
    ) : null;

    const renderTerminalSelection = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {capturedImage
                    ? <img
                        src={capturedImage}
                        alt="wiring"
                        className={classes.capturedPhotoConstrained}
                        onClick={() => openPhoto(capturedImage)}
                      />
                    : renderImage()
                }
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.terminalSelectionTitle', { defaultValue: 'Select your terminals' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {isHeatPump === true 
                        ? I18n.t('mobile.AirDeviceProvisioning.terminalSelectionSubtitleHeatPump', { defaultValue: 'As you have a Heat Pump, follow the Heat Pump labels and tap every terminal with a wire connected.' })
                        : isHeatPump === false
                        ? I18n.t('mobile.AirDeviceProvisioning.terminalSelectionSubtitleConventional', { defaultValue: 'As you have a Conventional system, follow the Conventional labels and tap every terminal with a wire connected.' })
                        : I18n.t('mobile.AirDeviceProvisioning.terminalSelectionSubtitle', { defaultValue: 'Tap every terminal letter that has a wire connected.' })
                    }
                </Typography>
                <Box className={classes.chipsSection}>
                    <Box className={classes.terminalGroupsGrid}>
                        {TERMINAL_GROUPS_MAIN.map(group => (
                            <Box key={group.label} className={classes.terminalGroupCard}>
                                <Typography className={classes.chipGroupLabel}>{group.label}</Typography>
                                <Box className={classes.chipCircleRow}>
                                    {group.terminals.map(t => (
                                        <Box
                                            key={t}
                                            className={classes.chipCircleItem}
                                            onClick={() => toggleTerminal(t)}
                                        >
                                            <Box className={selectedTerminals.includes(t) ? classes.chipCircleSelected : classes.chipCircle}>
                                                {selectedTerminals.includes(t) && (
                                                    <span className={classes.chipCircleCheck}>
                                                        <Icon src='blueTick' />
                                                    </span>
                                                )}
                                            </Box>
                                            <Typography className={classes.chipCircleLabel}>{t}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        ))}
                        {showAccessories && (
                            <Box className={`${classes.terminalGroupCard} ${classes.terminalGroupCardFull}`}>
                                <Typography className={classes.chipGroupLabel}>{TERMINAL_GROUP_ACCESSORIES.label}</Typography>
                                <Box className={classes.chipCircleRowGrid}>
                                    {TERMINAL_GROUP_ACCESSORIES.terminals.map(t => (
                                        <Box
                                            key={t}
                                            className={classes.chipCircleItem}
                                            onClick={() => toggleTerminal(t)}
                                        >
                                            <Box className={selectedTerminals.includes(t) ? classes.chipCircleSelected : classes.chipCircle}>
                                                {selectedTerminals.includes(t) && (
                                                    <span className={classes.chipCircleCheck}>
                                                        <Icon src='blueTick' />
                                                    </span>
                                                )}
                                            </Box>
                                            <Typography className={classes.chipCircleLabel}>{t}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>
                    <button className={classes.showMoreBtn} onClick={() => setShowAccessories(v => !v)}>
                        {showAccessories
                            ? I18n.t('mobile.AirDeviceProvisioning.showFewerTerminals', { defaultValue: '- Show fewer terminals' })
                            : I18n.t('mobile.AirDeviceProvisioning.showMoreTerminals', { defaultValue: '+ Show more terminals' })
                        }
                    </button>
                </Box>
            </Box>
            {renderBottomContinue(handleTerminalContinue, I18n.t('mobile.common.continue'), selectedTerminals.length === 0)}
        </>
    );

    const renderUncommonWiring = () => renderErrorScreen(
        I18n.t('mobile.AirDeviceProvisioning.uncommonWiringTitle', { defaultValue: 'Uncommon wiring detected' }),
        I18n.t('mobile.AirDeviceProvisioning.uncommonWiringBody', { defaultValue: "We couldn't match your wiring to a standard HVAC configuration. Please review your wiring or contact our support team for help." }),
        true
    );

    const renderKTerminal = () => renderErrorScreen(
        I18n.t('mobile.AirDeviceProvisioning.kTerminalTitle', { defaultValue: 'Professional rewiring required' }),
        I18n.t('mobile.AirDeviceProvisioning.kTerminalBody', { defaultValue: 'Your thermostat has a K terminal, indicating a Honeywell wire-saving module. A technician will need to rewire to standard terminals before installing the IQ Air.' }),
        true
    );

    const renderUnrecognized = () => renderErrorScreen(
        I18n.t('mobile.AirDeviceProvisioning.unrecognizedTitle', { defaultValue: 'Unrecognized terminal' }),
        I18n.t('mobile.AirDeviceProvisioning.unrecognizedBody', { defaultValue: "We couldn't identify all of your terminals. Please contact our support team to guide you through the rest of the installation process." }),
        true
    );

    const renderRemoveThermostat = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igRemoveThermostat')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.removeThermostatTitle', { defaultValue: 'Remove the old thermostat' })}
                </Typography>
                <ol className={classes.numberedList}>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.removeThermostatStep1', { defaultValue: 'Loosen each terminal and carefully disconnect each wire.' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.removeThermostatStep2', { defaultValue: 'Uninstall the thermostat base from the wall.' })}</li>
                </ol>
            </Box>
            {renderBottomContinue(() => goTo(STEPS.INSTALL_TRIM_PLATE))}
        </>
    );

    const renderInstallTrimPlate = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igTrimPlate')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.trimPlateTitle', { defaultValue: 'Optional: Install the trim plate' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.trimPlateBody', { defaultValue: 'If your wall has marks or holes from the old thermostat, attach the trim plate(1) to cover them before mounting.' })}
                </Typography>
            </Box>
            {renderBottomContinue(() => goTo(STEPS.MOUNT_BASE))}
        </>
    );

    const renderMountBase = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igMountBase')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.mountBaseTitle', { defaultValue: 'Mount the base to the wall' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.mountBaseBody', { defaultValue: 'If needed, drill two holes with a 3/16" bit and insert the drywall anchors before securing the base.' })}
                </Typography>
            </Box>
            {renderBottomContinue(() => goTo(STEPS.CONNECT_WIRES))}
        </>
    );

    const renderConnectWires = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {capturedImage
                    ? <img src={capturedImage} alt="wiring" className={classes.capturedPhotoConstrained} onClick={() => openPhoto(capturedImage)} />
                    : renderImage()
                }
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.connectWiresTitle', { defaultValue: 'Connect your wires' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.connectWiresBody', { defaultValue: 'Match each wire from your old thermostat to the corresponding terminal on the IQ Air.' })}
                </Typography>
                {wiringMap.length > 0 && (
                    <Box className={classes.wiringTable}>
                        <Box className={`${classes.wiringTableHeader} ${isDarkMode ? classes.wiringArrowDarkColor : ''}`}>
                            <Typography className={classes.wiringColLabel}>
                                {I18n.t('mobile.AirDeviceProvisioning.oldTerminal', { defaultValue: 'Old terminal' })}
                            </Typography>
                            <span className={`${classes.wiringArrow}`}><Icon src='IgRightArrow' /></span>
                            {/* <Typography className={classes.wiringColLabel} style={{ flex: 0.3 }}> </Typography> */}
                            <Typography className={classes.wiringColLabel}>
                                {I18n.t('mobile.AirDeviceProvisioning.iqAirTerminal', { defaultValue: 'IQ Air terminal' })}
                            </Typography>
                        </Box>
                        {[...wiringMap].sort((a, b) => (a.note ? 1 : 0) - (b.note ? 1 : 0)).map((row, i) => (
                            <Box key={i} className={` ${classes.wiringRow} ${isDarkMode ? classes.wiringArrowDarkColor : ''}`}>
                                <span className={`${classes.wiringChipOld} ${row.note ? classes.wiringChipOldCWire : ''}`}>{row.old}</span>
                                <span className={`${classes.wiringArrow}`}><Icon src='IgRightArrow' /></span>
                                {row.action === 'tape-off'
                                    ? <span className={`${classes.wiringChipNew} ${classes.tapeOff}`}>Tape off</span>
                                    : <span className={`${classes.wiringChipNew} ${row.note ? classes.wiringChipCWire : ''}`}>{row['new']}</span>
                                }
                            </Box>
                        ))}
                    </Box>
                )}
                {wiringMap.some(row => row.note) && (
                    <Box className={classes.infoBanner}>
                        <Box className={classes.cWireNoteRow}>
                            <span className={classes.cWireCircle} />
                            <Typography className={classes.infoBannerText}>
                                {I18n.t('mobile.AirDeviceProvisioning.unusedWireCNote', { defaultValue: 'This is the unused wire you will use as your C-wire.' })}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
            {renderBottomContinue(() => goTo(STEPS.RESTORE_POWER))}
        </>
    );

    const renderRestorePower = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igRestorePower')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.restorePowerTitle', { defaultValue: 'Restore HVAC power' })}
                </Typography>
                <Typography dangerouslySetInnerHTML={{__html: I18n.t('mobile.AirDeviceProvisioning.restorePowerText', { defaultValue: 'After all wires are inserted,restore power to the HVAC system.'})}} className={classes.subtitle}></Typography>
                
            </Box>
            {renderBottomContinue(() => goTo(STEPS.CHECK_LED))}
        </>
    );

    const renderCheckLed = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igCheckLed')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.checkLedTitle', { defaultValue: 'Is the status LED lighting up?' })}
                </Typography>
                <Typography dangerouslySetInnerHTML={{__html: I18n.t('mobile.AirDeviceProvisioning.ledText', { defaultValue: 'Verify that the status LED on the <br/>base is on, confirming 24 VAC power.'})}} className={classes.subtitle}>
                    
                </Typography>
            </Box>
            {renderYesNo(
                () => goTo(STEPS.LED_ERROR),
                () => goTo(isPowerPlateFlow ? STEPS.PP_ATTACH_COVER : STEPS.ATTACH_DISPLAY)
            )}
        </>
    );

    const renderLedError = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('IgTurnOnHvacError')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.ledErrorTitle', { defaultValue: "Status LED isn't lighting up?" })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.ledErrorFollowSteps', { defaultValue: 'Follow these steps:' })}
                </Typography>
                <ol className={classes.numberedList} style={{padding: '0 62px'}}>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.ledErrorStep1', { defaultValue: 'Turn off HVAC power' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.ledErrorStep2', { defaultValue: 'Verify RH and C wires are securely inserted into the appropriate terminal' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.ledErrorStep3', { defaultValue: 'RH and C wires are required. If there is only a single R wire at the wall, use RH. (RC will not supply power to the thermostat)' })}</li>
                    <li>{I18n.t('mobile.AirDeviceProvisioning.ledErrorStep4', { defaultValue: 'Restore power to the HVAC system' })}</li>
                </ol>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.ledErrorContactNote', { defaultValue: 'If the status LED is still not lighting up, contact support.' })}
                </Typography>
            </Box>
            {renderRetryAndContactSupport()}
        </>
    );

    const renderAttachDisplay = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('igAttachDisplay')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.attachDisplayTitle', { defaultValue: 'Attach the display' })}
                </Typography>
                <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.attachDisplayBody', { defaultValue: 'Align the display with the base, and magnets will pull the display into place.' })}
                </Typography>
            </Box>
            {renderBottomContinue(() => goTo(STEPS.ON_SCREEN_INSTRUCTIONS))}
        </>
    );

    const renderOnScreenInstructions = (clickFunc) => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('IgSetupComplete')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.onScreenInstructionsTitle', { defaultValue: 'Follow the on-screen instructions' })}
                </Typography>
               {isIQAirDisplay ? 
              <Box className={classes?.IQAirDisplayBox}>
                  <Typography className={classes?.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.iqAirDisplayZwPairing', { defaultValue: 'After powering on, IQ Air for secondary zone control will walk you through the setup process. You\'ll also need to connect it to the IQ Air for primary zone control to control it from the Enlighten app.' })}
                  </Typography>
                  <Typography className={classes?.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.followTheseSteps', { defaultValue: 'IQ Air for secondary zone control will prompt you to begin Z-Wave pairing. Complete these steps to connect your units:' })}
                  </Typography>
                  <ol className={classes?.numberedList}>
                    <li dangerouslySetInnerHTML={{__html: I18n.t('mobile.AirDeviceProvisioning.iqAirDisplayStep1', { defaultValue: 'On IQ Air primary zone control, navigate to:<strong>Menu → Installer → Smart Home → Add Device</strong>' })}}></li>
                    <li dangerouslySetInnerHTML={{__html: I18n.t('mobile.AirDeviceProvisioning.iqAirDisplayStep2', { defaultValue: 'On IQ Air secondary zone control, Tap <strong>Add Device</strong> during setup, or go to: <strong>Menu → Installer → Z-Wave → Add to Network</strong>' })}}></li>
                  </ol>
                  <Typography className={classes?.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.iqAirDisplaySetupCompleteNote', { defaultValue: 'Pairing is complete when your IQ Air confirms the device has been added.' })}
                  </Typography>
                </Box>
               : <Typography className={classes.subtitle}>
                    {I18n.t('mobile.AirDeviceProvisioning.onScreenInstructionsBody', { defaultValue: 'After powering on, IQ Air will walk you through the setup process. Setup is complete when you reach the thermostat\'s home screen.' })}
                </Typography>}
                <Box className={classes.dualFuelBanner}>
                    <Typography className={classes.dualFuelBannerText}>
                        {I18n.t('mobile.AirDeviceProvisioning.dualFuelBannerText', { defaultValue: 'If you have two different heat sources and are unsure what to select as heat type,' })}{' '}
                        <span className={classes.learnMoreLink} onClick={() => goTo(STEPS.DUAL_FUEL_EXPLAIN)}>
                            {I18n.t('mobile.AirDeviceProvisioning.clickHere', { defaultValue: 'click here' })}
                        </span>.
                    </Typography>
                </Box>
            </Box>
            {renderBottomContinue(() => {
                if (isIQAirDisplay) {
                    return goTo(STEPS.SERVICE_PROVIDER_PIN);
                }
                return goTo(STEPS.SETUP_COMPLETE);
            })}
           
        </>
    );

    // ── 16.0 Service provider PIN (Secondary only) ────────────────────────
    const renderServiceProviderPin = (clickFunc) => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('IgSetupComplete')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.serviceProviderPinTitle', { defaultValue: 'Enter service provider PIN' })}
                </Typography>
                <Typography dangerouslySetInnerHTML={{__html: I18n.t('mobile.AirDeviceProvisioning.serviceProviderPinBody', { defaultValue: 'On your IQ Air secondary zone thermostat, go to:<br/><strong style="font-weight:700">Menu → Installer → Advanced Settings →<br/>Service Provider Information</strong><br/>Enter PIN: <strong style="font-weight:700">30332</strong>' })}} className={classes.serviceProviderPinBody}></Typography>
                <Typography className={classes.subtitle} style={{ textAlign: 'center' }}>
                    {I18n.t('mobile.AirDeviceProvisioning.serviceProviderPinWait', { defaultValue: 'Wait for the settings to apply before continuing.' })}
                </Typography>
            </Box>
            {renderBottomContinue(() => {
                if (typeof clickFunc === 'function') {
                    return clickFunc(PAGES.LINK_HVAC_TO_THERMOSTAT);
                }
                return goTo(STEPS.SETUP_COMPLETE);
            })}
        </>
    );

    // ── Explanation screens (Learn more / Click here) ──────────────────────
    const renderJumperExplain = () => (
        <>
            <Box className={classes.body}>
                {renderImage('igJumperWires', isDarkMode)}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.jumperExplainTitle', { defaultValue: 'What is a jumper wire?' })}
                </Typography>
                <Typography className={classes.explainBody}>
                    {I18n.t('mobile.AirDeviceProvisioning.jumperExplainIntro', { defaultValue: 'A jumper wire is a short wire that connects two terminals on your old thermostat - most commonly RH and RC.' })}
                </Typography>
                <Typography className={classes.explainSubheading}>
                    {I18n.t('mobile.AirDeviceProvisioning.jumperExplainIdentifyTitle', { defaultValue: 'How do I identify one?' })}
                </Typography>
                <Typography className={classes.explainBody}>
                    {I18n.t('mobile.AirDeviceProvisioning.jumperExplainIdentifyBody', { defaultValue: 'A jumper wire is usually a short, loose wire that connects two terminals directly - it doesn\u2019t go into the wall with the rest of the wire bundle.' })}
                </Typography>
                <Typography className={classes.explainSubheading}>
                    {I18n.t('mobile.AirDeviceProvisioning.jumperExplainRemoveTitle', { defaultValue: 'Why should I remove it?' })}
                </Typography>
                <Typography className={classes.explainBody}>
                    {I18n.t('mobile.AirDeviceProvisioning.jumperExplainRemoveBody', { defaultValue: 'The IQ Air handles this connection automatically through software. It creates an internal digital jumper - no physical wire needed.' })}
                </Typography>
                <Box className={classes.warningBanner}>
                    <Typography className={classes.warningBannerText}>
                        {I18n.t('mobile.AirDeviceProvisioning.jumperExplainWarning', { defaultValue: 'Important: Leaving a physical jumper in place can cause a short circuit and damage the system.' })}
                    </Typography>
                </Box>
            </Box>
            <Box className={classes.buttonContainer}>
                <Button className={classes.secondaryButton} onClick={goBack}>
                    {I18n.t('mobile.AirDeviceProvisioning.backBtn', { defaultValue: 'Back' })}
                </Button>
            </Box>
        </>
    );

    const renderHeatPumpExplain = () => (
        <>
            <Box className={classes.body}>
                {renderImage('igHeatPumpCheck')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.heatPumpExplainTitle', { defaultValue: 'How to tell if you have a heat pump' })}
                </Typography>
                <Typography className={classes.explainSubheading}>
                    {I18n.t('mobile.AirDeviceProvisioning.heatPumpExplainStep1Title', { defaultValue: 'Check the outdoor unit' })}
                </Typography>
                <Typography className={classes.explainBody}>
                    {I18n.t('mobile.AirDeviceProvisioning.heatPumpExplainStep1Body', { defaultValue: 'Look at the make and model label on your outdoor HVAC unit. Search the model number online or check the manufacturer\u2019s manual - it will tell you whether it\u2019s a heat pump or not.' })}
                </Typography>
                <Typography className={classes.explainSubheading}>
                    {I18n.t('mobile.AirDeviceProvisioning.heatPumpExplainStep2Title', { defaultValue: 'Check your current thermostat settings' })}
                </Typography>
                <Typography className={classes.explainBody}>
                    {I18n.t('mobile.AirDeviceProvisioning.heatPumpExplainStep2Body', { defaultValue: 'Your existing thermostat needs to know whether it\u2019s running a heat pump, so the system type should already be set somewhere in its menus.' })}
                </Typography>
                <Typography className={classes.explainSubheading}>
                    {I18n.t('mobile.AirDeviceProvisioning.heatPumpExplainStep3Title', { defaultValue: 'Run the system in heat mode' })}
                </Typography>
                <Typography className={classes.explainBody}>
                    {I18n.t('mobile.AirDeviceProvisioning.heatPumpExplainStep3Body', { defaultValue: 'Set your thermostat to heat mode and turn the temperature up. Go outside, if the outdoor unit turns on you have a heat pump and check if the outdoor unit turns on. If it does, you have a heat pump - conventional systems only use the outdoor unit for cooling. If it\u2019s very cold outside, the heat pump\u2019s compressor may not run even in heat mode, so this test works best in mild weather.' })}
                </Typography>
            </Box>
            <Box className={classes.buttonContainer}>
                <Button className={classes.secondaryButton} onClick={goBack}>
                    {I18n.t('mobile.AirDeviceProvisioning.backBtn', { defaultValue: 'Back' })}
                </Button>
            </Box>
        </>
    );

    const renderDualFuelExplain = () => (
        <>
            <Box className={classes.body}>
                {renderImage('IgSetupComplete')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.dualFuelExplainTitle', { defaultValue: 'Choosing your heat type' })}
                </Typography>
                <Typography className={classes.explainBody}>
                    {I18n.t('mobile.AirDeviceProvisioning.dualFuelExplainSubtitle', { defaultValue: 'For dual fuel systems, the heat type setting refers to your' })}{' '}
                    <span className={classes.boldAccent}>{I18n.t('mobile.AirDeviceProvisioning.dualFuelExplainBackupBold', { defaultValue: 'backup heat source' })}</span>
                    {I18n.t('mobile.AirDeviceProvisioning.dualFuelExplainNotHeatPump', { defaultValue: ' — not the heat pump.' })}
                </Typography>
                <Box className={classes.explainCard}>
                    <ul>
                        <li>
                            <span className={classes.boldAccent}>{I18n.t('mobile.AirDeviceProvisioning.dualFuelFossilLabel', { defaultValue: 'Fossil' })}</span>
                            {I18n.t('mobile.AirDeviceProvisioning.dualFuelFossilDesc', { defaultValue: ' — Select if your backup is a gas or oil furnace boiler, or radiant. The thermostat does not activate the fan — the heating equipment controls its own blower.' })}
                        </li>
                        <li>
                            <span className={classes.boldAccent}>{I18n.t('mobile.AirDeviceProvisioning.dualFuelElectricLabel', { defaultValue: 'Electric' })}</span>
                            {I18n.t('mobile.AirDeviceProvisioning.dualFuelElectricDesc', { defaultValue: ' — Select if your backup uses heat strips or an electric furnace. The thermostat activates the fan simultaneously with the heating call.' })}
                        </li>
                    </ul>
                </Box>
            </Box>
            <Box className={classes.buttonContainer}>
                <Button className={classes.secondaryButton} onClick={goBack}>
                    {I18n.t('mobile.AirDeviceProvisioning.backBtn', { defaultValue: 'Back' })}
                </Button>
            </Box>
        </>
    );

    const renderSetupComplete = () => (
        <>
            <Box className={classes.body}>
                {renderStepper()}
                {renderImage('systemSetupSuccess')}
                <Typography className={classes.title}>
                    {I18n.t('mobile.AirDeviceProvisioning.setupCompleteTitle', { defaultValue: 'IQ Air is successfully installed' })}
                </Typography>
                <Box className={classes.photoHintBox}>
                    <Typography className={classes.subtitle}>
                        {I18n.t('mobile.AirDeviceProvisioning.setupCompleteWifiNote', { defaultValue: 'For improved connectivity, add Wi-Fi. On the Enphase Air display, go to' })} <span style={{fontWeight:'500'}}>Menu &gt; Wi-Fi</span> {I18n.t('mobile.AirDeviceProvisioning.setupCompleteWifiNote2', { defaultValue: 'and enter your credentials.' })}
                    </Typography>
                    <Typography className={classes.subtitle}>
                        To view the <span style={{fontWeight:'500'}}>live status</span> of your Enphase Energy system, <span style={{fontWeight:'500'}}>tap the Enphase logo</span> on the home screen. You can also access it under <span style={{fontWeight:'500'}}>Menu &gt; Energy</span>.
                    </Typography>
                </Box>
            </Box>
            <Box className={classes.buttonContainer}>
                <Button className={classes.primaryButton} onClick={onViewSystem}>
                    {I18n.t('mobile.AirDeviceProvisioning.viewSystemBtn', { defaultValue: 'View System' })}
                </Button>
            </Box>
        </>
    );

    // ── Screen dispatch ───────────────────────────────────────────────────────
    const renderContent = (handleClick) => {
        // Delegate No C-Wire flow (POWER_PLATE, UW_*, PP_*) to NoCWireFlow
        if (NO_C_WIRE_STEPS.has(currentStep)) {
            return (
                <NoCWireFlow
                    currentStep={currentStep}
                    classes={classes}
                    goTo={goTo}
                    goBack={goBack}
                    renderStepper={renderStepper}
                    renderImage={renderImage}
                    renderBottomContinue={renderBottomContinue}
                    renderYesNo={renderYesNo}
                    selectedTerminals={selectedTerminals}
                    wiringMap={wiringMap}
                    setWiringMap={setWiringMap}
                    unusedWirePhoto={unusedWirePhoto}
                    setUnusedWirePhoto={setUnusedWirePhoto}
                    unusedWirePhotoName={unusedWirePhotoName}
                    setUnusedWirePhotoName={setUnusedWirePhotoName}
                    capturePhoto={capturePhoto}
                    uploadPhoto={uploadPhoto}
                    handleFileSelect={handleFileSelect}
                    handleUnusedWireComplete={handleUnusedWireComplete}
                    isDarkMode={isDarkMode}
                    setIsPowerPlateFlow={setIsPowerPlateFlow}
                    STEPS={STEPS}
                    displayType={displayType}
                    isDualRNoC={isDualRNoC}
                    openPhoto={openPhoto}
                    renderPhotoOverlay={renderPhotoOverlay}
                    ppWireColors={ppWireColors}
                    setPpWireColors={setPpWireColors}
                    ppCustomColors={ppCustomColors}
                    setPpCustomColors={setPpCustomColors}
                    openURL={openURL}
                />
            );
        }

        switch (currentStep) {
            case STEPS.INTRO: return renderIntro();
            case STEPS.WHATS_IN_BOX: return renderWhatsInBox();
            case STEPS.TOOLS_NEEDED: return renderToolsNeeded();
            case STEPS.TURN_OFF_HVAC: return renderTurnOffHvac();
            case STEPS.HVAC_POWER_ERROR: return renderHvacPowerError();
            case STEPS.TAKE_PHOTO: return renderTakePhoto();
            case STEPS.PHOTO_ERROR: return renderPhotoError();
            case STEPS.HIGH_VOLTAGE: return renderHighVoltage();
            case STEPS.HIGH_VOLTAGE_ERROR: return renderHighVoltageError();
            case STEPS.JUMPER_WIRES: return renderJumperWires();
            case STEPS.REMOVE_JUMPERS: return renderRemoveJumpers();
            case STEPS.DUAL_LABELS: return renderDualLabels();
            case STEPS.HEAT_PUMP_CHECK: return renderHeatPumpCheck();
            case STEPS.TERMINAL_SELECTION: return renderTerminalSelection();
            case STEPS.UNCOMMON_WIRING: return renderUncommonWiring();
            case STEPS.K_TERMINAL: return renderKTerminal();
            case STEPS.UNRECOGNIZED: return renderUnrecognized();
            case STEPS.REMOVE_THERMOSTAT: return renderRemoveThermostat();
            case STEPS.INSTALL_TRIM_PLATE: return renderInstallTrimPlate();
            case STEPS.MOUNT_BASE: return renderMountBase();
            case STEPS.CONNECT_WIRES: return renderConnectWires();
            case STEPS.RESTORE_POWER: return renderRestorePower();
            case STEPS.CHECK_LED: return renderCheckLed();
            case STEPS.LED_ERROR: return renderLedError();
            case STEPS.ATTACH_DISPLAY: return renderAttachDisplay();
            case STEPS.ON_SCREEN_INSTRUCTIONS: return renderOnScreenInstructions(handleClick);
            case STEPS.SERVICE_PROVIDER_PIN: return renderServiceProviderPin(handleClick);
            case STEPS.SETUP_COMPLETE: return renderSetupComplete();
            case STEPS.JUMPER_EXPLAIN: return renderJumperExplain();
            case STEPS.HEAT_PUMP_EXPLAIN: return renderHeatPumpExplain();
            case STEPS.DUAL_FUEL_EXPLAIN: return renderDualFuelExplain();
            default: return null;
        }
    };

    return (
        <>
            <Header title={headerTitle} onClick={goBack} />
            <Box className={classes.container}>
                {renderContent(handleClick)}
            </Box>
            {renderPhotoOverlay()}
        </>
    );
};

export default InstallationGuide;
